import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowUpRight,
  Sparkles,
  Code2,
  Bot,
  Cpu,
  Braces,
  Zap,
} from "lucide-react";
import MaskedReveal from "@/components/site/MaskedReveal";
import { PROFILE } from "@/data/content";
import { scrollToId } from "@/hooks/useLenis";

const floaters = [
  { Icon: Code2, x: "8%", y: "18%", d: 0.2 },
  { Icon: Braces, x: "18%", y: "72%", d: 0.6 },
  { Icon: Bot, x: "72%", y: "12%", d: 0.9 },
  { Icon: Cpu, x: "84%", y: "62%", d: 0.4 },
  { Icon: Sparkles, x: "44%", y: "8%", d: 0.7 },
  { Icon: Zap, x: "58%", y: "82%", d: 0.3 },
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  const photoY = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const bgY = useTransform(scrollYProgress, [0, 1], [0, -120]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 60]);

  const handleTalk = () => scrollToId("contact");
  const handlePlayground = () => {
    if (typeof window !== "undefined") window.location.href = "/playground";
  };

  return (
    <section
      id="hero"
      ref={ref}
      className="relative min-h-[100svh] overflow-hidden pt-28 md:pt-32 pb-16"
      data-testid="hero-section"
    >
      {/* Ambient background gradient + particles */}
      <motion.div
        style={{ y: bgY }}
        aria-hidden="true"
        className="absolute inset-0 -z-10"
      >
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 h-[720px] w-[1200px] rounded-full bg-[radial-gradient(closest-side,rgba(79,110,247,0.18),transparent_70%)] blur-2xl" />
        <div className="absolute -bottom-40 right-[-10%] h-[520px] w-[820px] rounded-full bg-[radial-gradient(closest-side,rgba(124,92,246,0.16),transparent_70%)] blur-2xl" />
        <div className="hidden sm:block">
          <ParticleField />
        </div>
      </motion.div>

      <div className="section-x mx-auto max-w-[1400px] grid lg:grid-cols-12 gap-10 lg:gap-8 items-center">
        <motion.div style={{ y: textY }} className="lg:col-span-7 relative">
          {/* Availability chip */}
          <motion.a
            href="#contact"
            onClick={(e) => {
              e.preventDefault();
              scrollToId("contact");
            }}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-400/[0.08] px-3 py-1.5 text-xs font-medium text-emerald-300"
            data-testid="hero-availability"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inset-0 rounded-full bg-emerald-400 animate-pulse-ring" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
            </span>
            {PROFILE.availability}
          </motion.a>

          <div className="mt-6 md:mt-8">
            <MaskedReveal
              lines={[
                "Chirag",
                <>
                  Khimani<span className="text-accent-cyan">.</span>
                </>,
              ]}
              className="text-[13vw] sm:text-[11vw] md:text-[9.5vw] lg:text-[8vw] font-semibold tracking-[-0.03em]"
              delay={0.15}
              stagger={0.08}
            />
          </div>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55, duration: 0.7 }}
            className="mt-5 text-ink/70 text-base md:text-lg max-w-xl leading-relaxed"
            data-testid="hero-role"
          >
            {PROFILE.role}
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.7 }}
            className="mt-4 text-ink/72 max-w-xl md:text-[15px] leading-relaxed"
          >
            {PROFILE.tagline}
          </motion.p>

          {/* Quick stat pills */}
          <motion.ul
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9, duration: 0.6 }}
            className="mt-7 flex flex-wrap gap-2"
            data-testid="hero-stat-pills"
          >
            {[
              "AI Powered Test Automation Expert",
              "12+ Years Experience",
              "5,000+ Students Trained",
              "2 Published Books",
            ].map((s) => (
              <li
                key={s}
                className="rounded-full border border-ink/10 bg-ink/[0.03] px-3 py-1.5 text-xs text-ink/70"
              >
                {s}
              </li>
            ))}
          </motion.ul>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.6 }}
            className="mt-9 flex flex-wrap items-center gap-3"
          >
            <button
              onClick={handleTalk}
              data-testid="hero-cta-talk"
              className="group relative inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-blue-500 to-violet-500 text-white px-6 py-3.5 text-sm font-semibold shadow-[0_8px_30px_rgba(139,92,246,0.35)] hover:shadow-[0_12px_40px_rgba(139,92,246,0.55)] hover:from-blue-400 hover:to-violet-400 transition-[box-shadow,background-color] duration-300"
            >
              Let&apos;s Talk
              <ArrowUpRight
                size={16}
                className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              />
            </button>
            <button
              onClick={handlePlayground}
              data-testid="hero-cta-playground"
              className="group relative inline-flex items-center gap-2 rounded-full border border-ink/15 bg-ink/[0.03] px-6 py-3.5 text-sm font-semibold text-ink/90 hover:border-ink/40 hover:bg-ink/[0.06] transition-colors"
            >
              Try Automation Playground
              <ArrowUpRight
                size={16}
                className="opacity-70 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              />
            </button>
          </motion.div>
        </motion.div>

        {/* Photo column */}
        <motion.div
          style={{ y: photoY }}
          className="lg:col-span-5 relative"
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.35, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
        >
          <PhotoFrame />
          {/* Floating tech icons */}
          {floaters.map(({ Icon, x, y, d }, i) => (
            <motion.div
              key={i}
              className="absolute z-20 hidden sm:flex h-11 w-11 items-center justify-center rounded-2xl glass"
              style={{ left: x, top: y }}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1 + d, duration: 0.6 }}
            >
              <motion.span
                animate={{ y: [0, -8, 0] }}
                transition={{
                  duration: 4 + d * 2,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className="text-ink/80"
              >
                <Icon size={18} />
              </motion.span>
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-ink/55"
        aria-hidden="true"
      >
        <span className="text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <motion.span
          className="block h-8 w-px bg-gradient-to-b from-ink/40 to-transparent"
          animate={{ scaleY: [0.4, 1, 0.4] }}
          transition={{ duration: 2.4, repeat: Infinity }}
        />
      </motion.div>
    </section>
  );
}

function PhotoFrame() {
  return (
    <div className="relative mx-auto aspect-[4/5] w-full max-w-md">
      {/* Soft static gradient glow */}
      <div className="absolute -inset-5 rounded-[40px] bg-gradient-to-br from-accent-blue/30 via-accent-violet/25 to-accent-pink/20 blur-2xl opacity-70" />
      {/* Spotlight */}
      <div className="absolute -inset-8 rounded-full bg-[radial-gradient(closest-side,rgba(124,92,246,0.18),transparent_70%)] blur-2xl" />
      <div className="relative h-full w-full rounded-[32px] overflow-hidden border border-ink/10 bg-[#0a0a0c]">
        <img
          src={`${PROFILE.photo}?v=softblack1`}
          alt="Portrait of Chirag Khimani, QA Automation Architect"
          className="h-full w-full object-cover object-top contrast-[1.06] saturate-[1.08] brightness-[1.02]"
          loading="eager"
          decoding="async"
          data-testid="hero-photo"
        />
        {/* Soft black edge blend into site theme */}
        <div className="absolute inset-0 bg-[radial-gradient(125%_120%_at_50%_42%,transparent_55%,#0a0a0c_100%)]" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0c] via-[#0a0a0c]/20 to-transparent" />
        <div className="absolute inset-0 mix-blend-soft-light bg-[radial-gradient(120%_65%_at_50%_0%,rgba(124,92,246,0.28),transparent_60%)]" />
        {/* Corner labels */}
        <div className="absolute left-4 top-4 rounded-full border border-white/15 bg-black/40 px-3 py-1 text-[10px] uppercase tracking-[0.25em] text-white/80">
          Est. 2013
        </div>
        <div className="absolute right-4 bottom-4 rounded-full border border-white/15 bg-black/50 px-3 py-1 text-[11px] text-white/85 font-mono">
          v12.1 · SDET
        </div>
      </div>
    </div>
  );
}

function ParticleField() {
  // Deterministic set of blurry floating particles
  const dots = Array.from({ length: 12 }, (_, i) => {
    const seed = (i * 9301 + 49297) % 233280;
    const rand = seed / 233280;
    return {
      x: (rand * 100).toFixed(1),
      y: ((i * 37) % 100).toFixed(1),
      s: 2 + ((i * 3) % 6),
      d: (i % 5) * 0.4,
    };
  });
  return (
    <div className="absolute inset-0 overflow-hidden">
      {dots.map((p, i) => (
        <motion.span
          key={i}
          className="absolute rounded-full bg-accent-purple/40 blur-[1.5px]"
          style={{
            left: `${p.x}%`,
            top: `${p.y}%`,
            width: p.s,
            height: p.s,
            opacity: 0.35,
          }}
          animate={{ y: [0, -14, 0], opacity: [0.2, 0.7, 0.2] }}
          transition={{
            duration: 5 + p.d * 2,
            repeat: Infinity,
            delay: p.d,
            ease: "easeInOut",
          }}
        />
      ))}
    </div>
  );
}
